import React, { useState, useContext, useEffect, useRef } from 'react';
import { useParams, useNavigate, useNavigationType } from "react-router-dom";
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import Collapsible from './Collapsible';
import ImageScroller from './ImageScroller';
import { Link } from 'react-router-dom';
import PlatformRatings from './PlatformRatings';
import AdminRatingsEditor from './AdminRatingsEditor';
import countries from 'i18n-iso-countries';
import CardEventArtChatMini from './CardEventArtChatMini.js';
import PageHeader from './PageHeader.js';
import FollowingDropdown from './FollowingModal.js';
import { renderArtUserActionButtons } from './artUserActionButtons';
import { tagName, isGlobalManualTag } from './TagBadge';
import ArtTags from './ArtTags';
import { formatYear, formatDate } from './../utils.js'; // Import the global function
import { Button, Text, Loader, TextInput, Textarea, useMantineColorScheme } from "@mantine/core";

// Pill background: dark theme keeps the original opaque
// Opaque Mantine default in both schemes so the pill reads the
// same regardless of what surface it sits on. The old transparent
// overlay composited differently on plain body vs a tinted Paper.
const pillBg = () => 'var(--mantine-color-default)';

// Initialize country data for localization
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
countries.registerLocale(require("i18n-iso-countries/langs/ru.json"));

const ArtPage = ({ artId, handleLoginWithGoogle }) => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const myUser = useContext(MyUserContext);
  const { colorScheme } = useMantineColorScheme();

  const { id: pathId } = useParams();
  const id = pathId || artId; // Use artId as fallback if pathId is not provided

  const queryParams = new URLSearchParams(window.location.search);
  const link = queryParams.get('link');

  // Hydrate art + similars from sessionStorage on first render so a
  // back-nav (POP) finds the full page already rendered. Without this,
  // the component mounts with art=null, returns an empty tree,
  // ScrollRestoration runs against a 0px-tall document and clamps the
  // user to the top. By the time async fetches resolve and the real
  // DOM renders, the scroll opportunity is gone.
  const [art, setArt] = useState(() => {
    try {
      const cached = sessionStorage.getItem(`art-page:${id}`);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [similars, setSimilars] = useState(() => {
    try {
      const cached = sessionStorage.getItem(`art-similars:${id}`);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [isViewerOpen, setIsViewerOpen] = useState(false); // State to control image viewer visibility
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // State to track the current image index
  const [isCopyLinkCopied, setIsCopyLinkCopied] = useState(false);
  const copyLinkTimeoutRef = useRef(null);
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  useEffect(() => {
    if (!pageMenuOpen) return undefined;
    const onDown = (e) => {
      if (e.target.closest && e.target.closest('[data-page-menu]')) return;
      setPageMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [pageMenuOpen]);
  const [recalcLoading, setRecalcLoading] = useState(false);

  // Admin-only inline edit for thumbnail and gallery image URLs.
  const isAdmin = !!(myUser && myUser.myUserInfo && myUser.myUserInfo.myUserId === "4312f009-7502-43a6-9483-2824508fe96f");
  const [editingThumb, setEditingThumb] = useState(false);
  const [thumbDraft, setThumbDraft] = useState('');
  const [savingThumb, setSavingThumb] = useState(false);
  const [editingImages, setEditingImages] = useState(false);
  const [imagesDraft, setImagesDraft] = useState('');
  const [savingImages, setSavingImages] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraftEn, setDescDraftEn] = useState('');
  const [descDraftRu, setDescDraftRu] = useState('');
  const [savingDesc, setSavingDesc] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraftEn, setNameDraftEn] = useState('');
  const [nameDraftRu, setNameDraftRu] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [editingOtherLinks, setEditingOtherLinks] = useState(false);
  const [otherLinksDraft, setOtherLinksDraft] = useState('');
  const [savingOtherLinks, setSavingOtherLinks] = useState(false);

  const [metacriticSearchLoading, setMetacriticSearchLoading] = useState(false);
  const [metacriticSearchResults, setMetacriticSearchResults] = useState(null); // null = not searched, [] = no matches
  const [metacriticSearchError, setMetacriticSearchError] = useState(null);
  const [addingMetacriticUrl, setAddingMetacriticUrl] = useState(null); // URL currently being appended to otherPlatformsLinks
  const [livelibSearchLoading, setLivelibSearchLoading] = useState(false);
  const [livelibSearchResults, setLivelibSearchResults] = useState(null);
  const [livelibSearchError, setLivelibSearchError] = useState(null);
  const [addingLivelibUrl, setAddingLivelibUrl] = useState(null);
  const [goodreadsSearchLoading, setGoodreadsSearchLoading] = useState(false);
  const [goodreadsSearchResults, setGoodreadsSearchResults] = useState(null);
  const [goodreadsSearchError, setGoodreadsSearchError] = useState(null);

  const [kpSearchLoading, setKpSearchLoading] = useState(false);
  const [kpSearchResults, setKpSearchResults] = useState(null);
  const [kpSearchError, setKpSearchError] = useState(null);
  const [addingKpUrl, setAddingKpUrl] = useState(null);
  const [addingGoodreadsUrl, setAddingGoodreadsUrl] = useState(null);

  const [hotPositionDraft, setHotPositionDraft] = useState('');
  const [hotPositionSaving, setHotPositionSaving] = useState(false);
  const [hotPositionLoaded, setHotPositionLoaded] = useState(false);

  const [editingYearGenres, setEditingYearGenres] = useState(false);
  const [yearDraft, setYearDraft] = useState('');
  const [genresDraft, setGenresDraft] = useState('');
  const [savingYearGenres, setSavingYearGenres] = useState(false);

  const [editingAuthor, setEditingAuthor] = useState(false);
  const [authorDraft, setAuthorDraft] = useState('');
  const [savingAuthor, setSavingAuthor] = useState(false);

  // Admin-only: replace this art's id in artEvents with another art's id.
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceInput, setReplaceInput] = useState('');
  const [replaceCandidate, setReplaceCandidate] = useState(null); // resolved other art
  const [replaceLookupError, setReplaceLookupError] = useState(null);
  const [replacing, setReplacing] = useState(false);

  const extractArtUuid = (raw) => {
    const s = (raw || '').trim();
    if (!s) return null;
    const uuidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const m = s.match(uuidRe);
    return m ? m[0] : null;
  };

  // Load the current admin-pinned hotArt position once, when we know
  // which art we're on and that the caller is admin.
  useEffect(() => {
    if (!isAdmin || !id) return;
    let cancelled = false;
    axiosInstance.get(`/arts/${id}/hotPosition`)
      .then((res) => {
        if (cancelled) return;
        const p = res.data && res.data.position;
        setHotPositionDraft(p ? String(p) : '');
        setHotPositionLoaded(true);
      })
      .catch(() => { if (!cancelled) setHotPositionLoaded(true); });
    return () => { cancelled = true; };
  }, [id, isAdmin]);

  // Resolve the input (uuid or /arts/<uuid> URL) to an art and remember it for
  // the thumbnail preview + Replace button.
  useEffect(() => {
    if (!replaceOpen) return;
    const candidate = extractArtUuid(replaceInput);
    setReplaceLookupError(null);
    if (!candidate) { setReplaceCandidate(null); return; }
    if (candidate === id) {
      setReplaceCandidate(null);
      setReplaceLookupError('Must differ from current art');
      return;
    }
    let cancelled = false;
    axiosInstance.get(`/arts/${candidate}/`)
      .then((res) => { if (!cancelled) setReplaceCandidate(res.data); })
      .catch(() => { if (!cancelled) { setReplaceCandidate(null); setReplaceLookupError('Art not found'); } });
    return () => { cancelled = true; };
  }, [replaceInput, replaceOpen, id]);

  const handleReplaceInEvents = () => {
    if (!replaceCandidate) return;
    if (!window.confirm(`Replace artId in all artEvents: ${id} -> ${replaceCandidate.id}?`)) return;
    setReplacing(true);
    axiosInstance.post(`/arts/${id}/replaceInEvents`, { newArtId: replaceCandidate.id })
      .then((res) => {
        const { updated = 0, skipped = 0 } = res.data || {};
        alert(`Done: updated ${updated}, skipped (conflict) ${skipped}`);
        setReplaceOpen(false);
        setReplaceInput('');
        setReplaceCandidate(null);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setReplacing(false));
  };

  const handleSaveThumbnail = () => {
    const next = (thumbDraft || '').trim();
    if (!next) { alert('URL is required'); return; }
    setSavingThumb(true);
    axiosInstance.put(`/arts/${id}/thumbnailUrl`, { thumbnailUrl: next })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, thumbnailUrl: res.data.thumbnailUrl } : prev);
        setEditingThumb(false);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setSavingThumb(false));
  };

  const handleSaveImageUrls = () => {
    const list = (imagesDraft || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    setSavingImages(true);
    axiosInstance.put(`/arts/${id}/imageUrls`, { imageUrls: list })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, imageUrls: res.data.imageUrls || [] } : prev);
        setEditingImages(false);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setSavingImages(false));
  };

  const handleSaveOtherPlatformsLinks = () => {
    const list = (otherLinksDraft || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    setSavingOtherLinks(true);
    axiosInstance.put(`/arts/${id}/otherPlatformsLinks`, { otherPlatformsLinks: list })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, otherPlatformsLinks: res.data.otherPlatformsLinks || [] } : prev);
        setEditingOtherLinks(false);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setSavingOtherLinks(false));
  };

  const handleAddMetacriticUrl = (item, urlToAdd) => {
    if (!urlToAdd || addingMetacriticUrl) return;
    const existing = (art?.otherPlatformsLinks) || [];
    if (existing.includes(urlToAdd)) return;
    const next = [...existing, urlToAdd];
    setAddingMetacriticUrl(urlToAdd);
    // Save the URL first; if that succeeds and the Metacritic card
    // carried scores, upsert the critic + user platform ratings so
    // the admin doesn't have to retype them.
    axiosInstance.put(`/arts/${id}/otherPlatformsLinks`, { otherPlatformsLinks: next })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, otherPlatformsLinks: res.data.otherPlatformsLinks || next } : prev);
        const saveRating = (platform, score, count) => {
          if (typeof score !== 'number' || score <= 0) return null;
          const votesNum = typeof count === 'number' && count > 0 ? count : 0;
          return axiosInstance.post('/admin/rating', {
            artId: id,
            platform,
            rating: score,
            votesCount: votesNum,
          }).catch((err) => {
            console.error(`${platform} rating save failed:`, err?.response?.data || err.message);
          });
        };
        const critic = item?.criticScoreSummary || {};
        const user = item?.userScoreSummary || {};
        return Promise.all([
          saveRating('metacritic', critic.score, critic.reviewCount),
          saveRating('metacriticUser', user.score, user.reviewCount),
        ]);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setAddingMetacriticUrl(null));
  };

  const handleAddLivelibUrl = (item) => {
    const urlToAdd = item?.link || '';
    if (!urlToAdd || addingLivelibUrl) return;
    const existing = (art?.otherPlatformsLinks) || [];
    if (existing.includes(urlToAdd)) return;
    const next = [...existing, urlToAdd];
    setAddingLivelibUrl(urlToAdd);
    // Save the URL first; if that succeeds and the Livelib card
    // carried a rating, also upsert the Livelib platform rating so
    // the admin doesn't have to type it into the rating editor.
    axiosInstance.put(`/arts/${id}/otherPlatformsLinks`, { otherPlatformsLinks: next })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, otherPlatformsLinks: res.data.otherPlatformsLinks || next } : prev);
        const ratingNum = typeof item.rating === 'number' && item.rating > 0 ? item.rating : null;
        if (ratingNum !== null) {
          const votesNum = typeof item.ratingsCount === 'number' && item.ratingsCount > 0 ? item.ratingsCount : 0;
          return axiosInstance.post('/admin/rating', {
            artId: id,
            platform: 'livelib',
            rating: ratingNum,
            votesCount: votesNum,
          }).catch((err) => {
            console.error('Livelib rating save failed:', err?.response?.data || err.message);
          });
        }
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setAddingLivelibUrl(null));
  };

  const handleAddGoodreadsUrl = (item) => {
    const urlToAdd = item?.link || '';
    if (!urlToAdd || addingGoodreadsUrl) return;
    const existing = (art?.otherPlatformsLinks) || [];
    if (existing.includes(urlToAdd)) return;
    const next = [...existing, urlToAdd];
    setAddingGoodreadsUrl(urlToAdd);
    // Save the URL first; if that succeeds and the Goodreads card
    // carried a rating, also upsert the Goodreads platform rating so
    // the admin doesn't have to type it into the rating editor.
    axiosInstance.put(`/arts/${id}/otherPlatformsLinks`, { otherPlatformsLinks: next })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, otherPlatformsLinks: res.data.otherPlatformsLinks || next } : prev);
        const ratingNum = typeof item.rating === 'number' && item.rating > 0 ? item.rating : null;
        if (ratingNum !== null) {
          const votesNum = typeof item.ratingsCount === 'number' && item.ratingsCount > 0 ? item.ratingsCount : 0;
          return axiosInstance.post('/admin/rating', {
            artId: id,
            platform: 'goodreads',
            rating: ratingNum,
            votesCount: votesNum,
          }).catch((err) => {
            console.error('Goodreads rating save failed:', err?.response?.data || err.message);
          });
        }
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setAddingGoodreadsUrl(null));
  };

  const handleSaveYearGenres = () => {
    const body = {};
    if (yearDraft !== '') {
      const y = parseInt(yearDraft, 10);
      if (Number.isNaN(y) || y < 0) {
        alert('Year must be a positive integer, or empty to keep');
        return;
      }
      body.year = y;
    }
    // Genres textarea: one per line; blanks and duplicates dropped server-side.
    // If the field is completely empty and the year is being set, we do NOT
    // wipe existing genres — only send genres when the user touched them.
    // Signal this by using `null` to mean "unchanged" and an empty array to
    // mean "clear all".
    if (genresDraft !== null) {
      body.genres = genresDraft.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    }
    if (body.year === undefined && body.genres === undefined) {
      setEditingYearGenres(false);
      return;
    }
    setSavingYearGenres(true);
    axiosInstance.put(`/arts/${id}/yearGenres`, body)
      .then(() => {
        setArt((prev) => prev ? {
          ...prev,
          ...(body.year !== undefined ? { year: body.year } : {}),
          ...(body.genres !== undefined ? { genres: body.genres } : {}),
        } : prev);
        setEditingYearGenres(false);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setSavingYearGenres(false));
  };

  const handleSaveAuthor = () => {
    const value = (authorDraft || '').trim();
    setSavingAuthor(true);
    axiosInstance.put(`/arts/${id}/author`, { author: value })
      .then(() => {
        // Backend serializes art.Author as JSON key "videoGameDeveloper"
        // (see logic/art/art.go:66) — mirror that name in local state or
        // reads elsewhere on the page (line ~1529) will show the old
        // value on next render.
        setArt((prev) => prev ? { ...prev, videoGameDeveloper: value } : prev);
        setEditingAuthor(false);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setSavingAuthor(false));
  };

  const handleSaveHotPosition = () => {
    const trimmed = (hotPositionDraft || '').trim();
    // Empty → send null so the backend removes the art from hot-arts
    // entirely. -1 → "auto" (add to hot-arts without a pin). >0 → pin.
    const parsed = trimmed === '' ? null : parseInt(trimmed, 10);
    if (parsed !== null && (Number.isNaN(parsed) || parsed < -1 || parsed === 0)) {
      alert('Position must be -1 (auto), a positive integer (pin), or empty (remove)');
      return;
    }
    setHotPositionSaving(true);
    axiosInstance.put(`/arts/${id}/hotPosition`, { position: parsed })
      .then(() => {})
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setHotPositionSaving(false));
  };

  const handleMetacriticSearch = () => {
    const q = (art?.name || '').trim();
    if (!q) return;
    setMetacriticSearchLoading(true);
    setMetacriticSearchError(null);
    axiosInstance.get(`/admin/metacriticSearch`, { params: { q } })
      .then((res) => {
        const items = (res.data && res.data.data && res.data.data.items) || [];
        setMetacriticSearchResults(items);
      })
      .catch((err) => {
        setMetacriticSearchError(err.response?.data || err.message);
        setMetacriticSearchResults([]);
      })
      .finally(() => setMetacriticSearchLoading(false));
  };

  const handleLivelibSearch = () => {
    // Prefer the Russian name for a Russian book platform, fall back to
    // the English name if that's all we have.
    const q = (art?.ruName || art?.name || '').trim();
    if (!q) return;
    setLivelibSearchLoading(true);
    setLivelibSearchError(null);
    axiosInstance.get(`/admin/livelibSearch`, { params: { q } })
      .then((res) => {
        setLivelibSearchResults(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        setLivelibSearchError(err.response?.data || err.message);
        setLivelibSearchResults([]);
      })
      .finally(() => setLivelibSearchLoading(false));
  };

  const handleKinopoiskSearch = () => {
    // KP is Russia-first — prefer the Russian name, fall back to English.
    const q = (art?.ruName || art?.name || '').trim();
    if (!q) return;
    setKpSearchLoading(true);
    setKpSearchError(null);
    axiosInstance.get(`/admin/kinopoiskSearch`, { params: { q } })
      .then((res) => {
        setKpSearchResults(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        setKpSearchError(err.response?.data || err.message);
        setKpSearchResults([]);
      })
      .finally(() => setKpSearchLoading(false));
  };

  const handleAddKpUrl = (item) => {
    const urlToAdd = item?.url || '';
    if (!urlToAdd || addingKpUrl) return;
    const existing = (art?.otherPlatformsLinks) || [];
    if (existing.includes(urlToAdd)) return;
    const next = [...existing, urlToAdd];
    setAddingKpUrl(urlToAdd);
    axiosInstance.put(`/arts/${id}/otherPlatformsLinks`, { otherPlatformsLinks: next })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, otherPlatformsLinks: res.data.otherPlatformsLinks || next } : prev);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setAddingKpUrl(null));
  };

  const handleGoodreadsSearch = () => {
    // Goodreads is English-first, so pass the English name first.
    const q = (art?.name || art?.ruName || '').trim();
    if (!q) return;
    setGoodreadsSearchLoading(true);
    setGoodreadsSearchError(null);
    axiosInstance.get(`/admin/goodreadsSearch`, { params: { q } })
      .then((res) => {
        setGoodreadsSearchResults(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        setGoodreadsSearchError(err.response?.data || err.message);
        setGoodreadsSearchResults([]);
      })
      .finally(() => setGoodreadsSearchLoading(false));
  };

  const handleSaveName = () => {
    setSavingName(true);
    axiosInstance.put(`/arts/${id}/name`, { name: nameDraftEn, ruName: nameDraftRu })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, name: res.data.name ?? prev.name, ruName: res.data.ruName ?? prev.ruName } : prev);
        setEditingName(false);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setSavingName(false));
  };

  const handleSaveDescription = () => {
    setSavingDesc(true);
    axiosInstance.put(`/arts/${id}/description`, { description: descDraftEn, ruDescription: descDraftRu })
      .then((res) => {
        setArt((prev) => prev ? { ...prev, description: res.data.description ?? prev.description, ruDescription: res.data.ruDescription ?? prev.ruDescription } : prev);
        setEditingDesc(false);
      })
      .catch((err) => alert('Error: ' + (err.response?.data || err.message)))
      .finally(() => setSavingDesc(false));
  };


  useEffect(() => {
    let cancelled = false;
    if (id) {
      // Scroll to top on fresh PUSH/REPLACE navigation. On POP (browser
      // back) React Router's <ScrollRestoration /> is restoring the
      // previous position — don't fight it with a forced scrollTo(0).
      if (navigationType !== 'POP') {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
      // React Router keeps ArtPage mounted across /arts/A -> /arts/B.
      // Without this synchronous re-hydrate, state still holds B's art +
      // similars when the user hits back to A, so A's page briefly shows
      // B's data until the async fetch resolves. Swap to A's cache in
      // the same tick as the id change; the fetch below just refreshes
      // it. null-fallback is intentional: no cache -> clear state so
      // we don't flash the previous page's data either.
      try {
        const cachedArt = sessionStorage.getItem(`art-page:${id}`);
        setArt(cachedArt ? JSON.parse(cachedArt) : null);
      } catch { setArt(null); }
      try {
        const cachedSim = sessionStorage.getItem(`art-similars:${id}`);
        setSimilars(cachedSim ? JSON.parse(cachedSim) : null);
      } catch { setSimilars(null); }
      try {
        const cachedPeople = sessionStorage.getItem(`art-people:${id}`);
        setOtherPeopleEvents(cachedPeople ? JSON.parse(cachedPeople) : null);
      } catch { setOtherPeopleEvents(null); }
      (async () => {
        const artData = await fetchArt();
        fetchSimilars(id);
        fetchOtherPeopleEvents(id);

        // Always refresh once after 3s to pick up arttouch side effects (e.g. rating refresh).
        const hasDescription = (d) => d && (d.description || d.ruDescription);
        if (!hasDescription(artData)) setLoadingDescription(true);
        await new Promise(r => setTimeout(r, 3000));
        if (cancelled) return;
        let data = await fetchArt();

        // Then up to 5 more refreshes with backoff while description is still empty.
        const extraDelays = [3000, 5000, 8000, 12000, 15000];
        for (const delay of extraDelays) {
          if (cancelled) break;
          if (hasDescription(data)) break;
          await new Promise(r => setTimeout(r, delay));
          if (cancelled) break;
          data = await fetchArt();
        }
        if (!cancelled) setLoadingDescription(false);
      })();
    } else {
      handleArtSubmit(link, () => cancelled);
    }
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    return () => {
      if (copyLinkTimeoutRef.current) {
        clearTimeout(copyLinkTimeoutRef.current);
      }
    };
  }, []);

  const [loadingDescription, setLoadingDescription] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false); // users, recommendations
  const [otherPeopleEvents, setOtherPeopleEvents] = useState(() => {
    // Same back-nav flash reason as art + similars — the engaged-friends
    // strip (paperplane row) and tag chips are driven off this list; if
    // it loads later than the rest of the page, the row flashes empty
    // then fills, bumping the Similars grid downward.
    try {
      const cached = sessionStorage.getItem(`art-people:${id}`);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  }); // null = loading, [] = none, [{...}] = list
  const [otherPeopleEventsLoading, setOtherPeopleEventsLoading] = useState(false);
  // Inline avatar strip on this page and the FollowingDropdown drawer
  // share these — recommendation lookups + per-session direct-chat
  // state — so both surfaces render the same action buttons and stay
  // in sync when the user acts from either place.
  const [selectedInlineUserId, setSelectedInlineUserId] = useState(null);
  const paperplaneSectionRef = useRef(null);
  const [recommendationsFromMeMap, setRecommendationsFromMeMap] = useState({});
  const [recommendationsToMeMap, setRecommendationsToMeMap] = useState({});
  const [messagesSentToUser, setMessagesSentToUser] = useState({});
  const [sendingToUser, setSendingToUser] = useState(null);

  const fetchOtherPeopleEvents = (artID) => {
    if (!myUser) {
      setOtherPeopleEvents([]);
      return;
    }
    setOtherPeopleEventsLoading(true);
    axiosInstance
      .get(`/arts/${artID}/otherPeopleEvents`)
      .then((response) => {
        const data = response.data || [];
        setOtherPeopleEvents(data);
        try { sessionStorage.setItem(`art-people:${artID}`, JSON.stringify(data)); } catch {}
      })
      .catch((error) => {
        console.error(error);
        setOtherPeopleEvents([]);
      })
      .finally(() => setOtherPeopleEventsLoading(false));
  };

  useEffect(() => {
    if (!art?.id || !myUser?.myUserInfo?.myUserId) return;
    let cancelled = false;
    Promise.all([
      axiosInstance.post('/getRecommendations/filters', {
        fromUserId: myUser.myUserInfo.myUserId,
        artId: art.id,
      }),
      axiosInstance.post('/getRecommendations/filters', {
        toUserId: myUser.myUserInfo.myUserId,
        artId: art.id,
      }),
    ])
      .then(([fromResponse, toResponse]) => {
        if (cancelled) return;
        const fromMap = {};
        fromResponse.data.forEach((rec) => {
          fromMap[rec.toUser.id] = rec.recommendation.id;
        });
        setRecommendationsFromMeMap(fromMap);
        const toMap = {};
        toResponse.data.forEach((rec) => {
          toMap[rec.fromUser.id] = rec.recommendation.id;
        });
        setRecommendationsToMeMap(toMap);
      })
      .catch((error) => console.error('Failed to fetch recommendations:', error));
    return () => { cancelled = true; };
  }, [art?.id, myUser?.myUserInfo?.myUserId]);

  const hasEvent = (userId) => {
    return !!(otherPeopleEvents || []).find((e) => e.userId === userId)?.artEventId;
  };
  const hasRecommendationFromMe = (userId) => recommendationsFromMeMap[userId] !== undefined;
  const hasRecommendationToMe = (userId) => recommendationsToMeMap[userId] !== undefined;

  const handleRecommend = (toUserIDs) => {
    const updatedMap = { ...recommendationsFromMeMap };
    Promise.all(
      toUserIDs.map((userId) =>
        axiosInstance
          .post('/postRecommendation', { toUserId: userId, artId: art.id })
          .then((response) => {
            updatedMap[userId] = response.data.recommendation.id;
          })
          .catch((error) => console.error(`Failed to recommend to user ${userId}:`, error))
      )
    ).then(() => setRecommendationsFromMeMap(updatedMap));
  };

  const navigateToDiscussion = (userId) => {
    const eventId = (otherPeopleEvents || []).find((e) => e.userId === userId)?.artEventId;
    navigate(`/artEvents/${eventId}`);
  };

  const sendArtLinkToUser = (userId) => {
    if (sendingToUser) return;
    setSendingToUser(userId);
    axiosInstance
      .post('/directChats', { otherUserId: userId })
      .then((res) => {
        const chatId = res.data?.chatId;
        if (!chatId) throw new Error('missing chatId in /directChats response');
        const text = `${window.location.origin}/arts/${art.id}`;
        return axiosInstance
          .post(`/messages?chatId=${chatId}&chatOwnerId=${userId}`, { text })
          .then(() => chatId);
      })
      .then((chatId) => {
        setMessagesSentToUser((prev) => ({ ...prev, [userId]: chatId }));
      })
      .catch((err) => console.error('Failed to send art link:', err))
      .finally(() => setSendingToUser(null));
  };

  const goToDirectChat = (userId) => {
    const chatId = messagesSentToUser[userId];
    if (!chatId) return;
    navigate(`/directChats/${chatId}?with=${userId}`);
  };

  useEffect(() => {
    if (!selectedInlineUserId) return;
    const onDown = (e) => {
      const el = paperplaneSectionRef.current;
      if (!el) return;
      // Wrapper is full-width for centering, so `contains` treats side
      // whitespace as inside. Test against child rects so clicks left/
      // right of the pill (or the buttons) actually deselect.
      const kids = Array.from(el.children);
      const x = e.clientX;
      const y = e.clientY;
      const inside = kids.some((child) => {
        const r = child.getBoundingClientRect();
        return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      });
      if (!inside) setSelectedInlineUserId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [selectedInlineUserId]);

  const fetchArt = () => {
    return axiosInstance
      .get(`/arts/${id}/`)
      .then((response) => {
        setArt(response.data);
        try { sessionStorage.setItem(`art-page:${id}`, JSON.stringify(response.data)); } catch {}
        return response.data;
      })
      .catch((error) => {
        console.error(error);
        return null;
      });
  };

  const handleArtSubmit = (link, isCancelled) => {
    axiosInstance
    .post(`/arts`, { wikiArticleLink: link })
    .then(async (response) => {
      let data = response.data;
      setArt(data);
      fetchSimilars(data.id);
      fetchOtherPeopleEvents(data.id);

      // Replace the current URL in the browser history
      // navigate({replace:true}) updates React Router's route params so
      // useParams().id resolves — plain replaceState wouldn't, which
      // left `id` undefined and made admin PUTs go to /arts/undefined/…
      navigate(`/arts/${data.id}`, { replace: true });

      // Always refresh once after 3s to pick up arttouch side effects (e.g. rating refresh).
      const hasDescription = (d) => d && (d.description || d.ruDescription);
      const artId = data.id;
      if (!hasDescription(data)) setLoadingDescription(true);
      await new Promise(r => setTimeout(r, 3000));
      if (isCancelled && isCancelled()) return;
      data = await axiosInstance.get(`/arts/${artId}/`).then(r => { setArt(r.data); return r.data; }).catch(() => null);

      // Then up to 5 more refreshes with backoff while description is still empty.
      const extraDelays = [3000, 5000, 8000, 12000, 15000];
      for (const delay of extraDelays) {
        if (isCancelled && isCancelled()) break;
        if (hasDescription(data)) break;
        await new Promise(r => setTimeout(r, delay));
        if (isCancelled && isCancelled()) break;
        data = await axiosInstance.get(`/arts/${artId}/`).then(r => { setArt(r.data); return r.data; }).catch(() => null);
      }
      if (!isCancelled || !isCancelled()) setLoadingDescription(false);
    })
    .catch((error) => {
      console.error(error);
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopyLinkCopied(true);
      if (copyLinkTimeoutRef.current) {
        clearTimeout(copyLinkTimeoutRef.current);
      }
      copyLinkTimeoutRef.current = setTimeout(() => {
        setIsCopyLinkCopied(false);
      }, 800);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSimilars = (artID, count = 60) => {
    axiosInstance
    .get(`/artsSimilar/${artID}/?count=${count}`)
    .then((response) => {
      setSimilars(response.data);
      // Cache per-artID so a back-nav to this page renders with the
      // same similars immediately (see initial useState above).
      try { sessionStorage.setItem(`art-similars:${artID}`, JSON.stringify(response.data)); } catch {}
    })
    .catch((error) => {
      console.error(error);
    })
  }

  const handleRecalcSimilars = (artID) => {
    setRecalcLoading(true);
    // Backend now enqueues resolution and returns 202 immediately —
    // matched/dropped counts aren't known yet, so there's nothing to
    // show. Just refresh similars so the user sees whatever's already
    // been resolved by the worker.
    axiosInstance
      .post(`/artsSimilar/${artID}/recalc`)
      .then(() => {
        setRecalcLoading(false);
        fetchSimilars(artID);
      })
      .catch((error) => {
        console.error(error);
        setRecalcLoading(false);
      });
  };

  // const handleEventTypeMove = (newEventType) => {
  //   axiosInstance
  //   .post(`/artEvents`, { artId: art.id, type: newEventType })
  //   .then((response) => {
  //     setArt(response.data.art);
  //     navigate("/artEvents/"+response.data.art.myEventId+"?tagsInitFocus=true");
  //   })
  //   .catch((error) => {
  //     console.error(error);
  //   });
  // };

  const formatDuration = (minutes, language) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return language === "ru"
      ? `${hours}ч ${mins} мин`
      : `${hours}h ${mins} min`;
  };

  // Video start.

  // Video viewer state + helpers
  const [isVideoViewerOpen, setIsVideoViewerOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const videos = art ? (art.videoUrls || []) : [];

  // Detect YouTube link and return embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      // youtu.be short link
      if (u.hostname === 'youtu.be') {
        const id = u.pathname.slice(1);
        return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
      }
      // youtube.com (watch?v=)
      if (u.hostname.includes('youtube.com')) {
        const v = u.searchParams.get('v');
        if (v) return `https://www.youtube.com/embed/${v}?autoplay=1&rel=0`;
        // handle /embed/ or other formats by returning the path after /embed/
        const pathParts = u.pathname.split('/');
        const embedIndex = pathParts.indexOf('embed');
        if (embedIndex !== -1 && pathParts[embedIndex + 1]) {
          return `https://www.youtube.com/embed/${pathParts[embedIndex + 1]}?autoplay=1&rel=0`;
        }
      }
    } catch (e) {
      // not a valid URL string — fallthrough
    }
    return null;
  };

  // quick heuristic to see if url is a direct playable video file
  const isDirectVideoFile = (url) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
  };

  const openVideoViewer = (index) => {
    // Hack youtube links to open in app if possible.
    // const current = videos[index];
    // const ytEmbed = getYouTubeEmbedUrl(current);
    // if (ytEmbed) {
    //   const watchUrl = ytEmbed.replace("/embed/", "/watch?v=");
    //   window.location.href = `youtube://www.youtube.com${watchUrl.replace("https://www.youtube.com", "")}`;
    //   setTimeout(() => window.open(watchUrl, "_blank"), 500);
    //   return;
    // }

    setCurrentVideoIndex(index);
    setIsVideoViewerOpen(true);
  };

  const closeVideoViewer = () => {
    setIsVideoViewerOpen(false);
  };

  const showNextVideo = () => {
    if (!videos || videos.length === 0) return;
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
  };

  const showPreviousVideo = () => {
    if (!videos || videos.length === 0) return;
    setCurrentVideoIndex((prevIndex) => (prevIndex - 1 + videos.length) % videos.length);
  };

  // Video end.

  const images = art ? (art.imageUrls && art.imageUrls.length > 0 ? art.imageUrls : [art.thumbnailUrl]) : [];

  const preloadImages = (startIndex, count) => {
    if (images) {
      for (let i = 0; i < count; i++) {
        const index = (startIndex + i) % images.length;
        const img = new Image();
        img.src = images[index];
      }
      // Prev direction too.
      for (let i = 0; i < count; i++) {
        const index = startIndex - i >= 0 ? startIndex - i : images.length + (startIndex - i);
        const img = new Image();
        img.src = images[index];
      }
    }
  };

  const openImageViewer = (index) => {
    setCurrentImageIndex(index);
    setIsViewerOpen(true);
  };

  const closeImageViewer = () => {
    setIsViewerOpen(false);
  };

  const showNextImage = () => {
    setCurrentImageIndex((prevIndex) => {
      const newIndex = (prevIndex + 1) % (images.length);
      preloadImages(newIndex, 2); // Preload the next 3 images
      return newIndex;
    });
  };

  const showPreviousImage = () => {
    setCurrentImageIndex((prevIndex) => {
      const newIndex = (prevIndex - 1 + (images.length || 1)) % (images.length || 1);
      preloadImages(newIndex, 2); // Preload the next 3 images
      return newIndex;
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      preloadImages(currentImageIndex, 5);
    }, 1000); // small delay ensures component fully loaded first
    return () => clearTimeout(timer);
  }, [currentImageIndex, art]);

  const renderDescription = (description) => {
    if (!description || description === '') {
      return null;
    }
    // Trust the source: honor whatever paragraph breaks it has and
    // stop there — no synthetic sentence-shuffling. The old algorithm
    // broke every ~250 chars, which was one paragraph per sentence
    // in Russian and read as a bulleted list. Any wall-of-text problem
    // now belongs to the source (backend / LLM), not this renderer.
    const paragraphs = description
      .split(/\r?\n/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    // Real <p> elements (not <br/><br/>) so first-line text-indent
    // applies per paragraph. Explicit margin between paragraphs
    // instead of the two-<br/> spacing.
    return paragraphs.map((p, index) => (
      <p
        key={index}
        style={{
          textIndent: '1.5em',
          marginTop: index === 0 ? 0 : '0.75em',
          marginBottom: 0,
        }}
      >
        {p}
      </p>
    ));
  };

  if (!art) {
    return (
      <>
      </>
    );
  }

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  return (
    <>
      <PageHeader
        title={`${localizeArt(art, language).name}${art.year ? ` (${art.year})` : ''}`}
        rightSlot={(
          <div className="relative" data-page-menu>
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setPageMenuOpen(v => !v); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPageMenuOpen(v => !v); } }}
              className="cursor-pointer select-none px-2"
              style={{ fontSize: 22, lineHeight: 1 }}
              aria-label={language === 'ru' ? 'Меню' : 'Menu'}
            >
              &#x22EF;
            </div>
            {pageMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-56 rounded shadow-lg"
                style={{ background: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)', zIndex: 60 }}
              >
                <div
                  className="px-4 py-2 cursor-pointer text-sm"
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--mantine-color-default-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}
                  onClick={() => { handleCopyLink(); setPageMenuOpen(false); }}
                >
                  {language === 'ru'
                    ? (isCopyLinkCopied ? 'Ссылка скопирована ✔' : 'Скопировать ссылку')
                    : (isCopyLinkCopied ? 'Link copied ✔' : 'Copy link')}
                </div>
              </div>
            )}
          </div>
        )}
      />
    <div
      key={art.id}
      className="container mx-auto mt-4 md:mt-8 px-4 md:px-8 w-full no-scrollbar overflow-hidden"
    >
        <div className="flex justify-center relative">
          <div className="relative inline-block"> {/* make wrapper shrink to image */}
            <img
              className="max-h-96 cursor-pointer block"
              src={art.thumbnailUrl}
              onClick={() => openImageViewer(0)}
            />
            {isAdmin && !editingThumb && (
              <div style={{ position: 'absolute', top: 4, right: 4 }}>
                <Button
                  size="compact-xs"
                  variant="light"
                  onClick={(e) => {
                    e.stopPropagation();
                    setThumbDraft(art.thumbnailUrl || '');
                    setEditingThumb(true);
                  }}
                >
                  Edit thumbnail
                </Button>
              </div>
            )}
            {videos && videos.length > 0 && (
              <div
                className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-sm px-2 py-1 rounded cursor-pointer hover:bg-opacity-90 flex items-center space-x-1"
                style={{ maxWidth: '100%' }} // ensure it never exceeds img width
                onClick={(e) => {
                  e.stopPropagation();
                  openVideoViewer(0);
                }}
              >
                <span aria-hidden>📹</span>
                <span className="truncate">{language === "ru" ? "смотреть видео" : "watch video"}</span>
              </div>
            )}
          </div>
        </div>

        {isAdmin && (editingThumb || editingImages) && (
          <div className="mx-auto mt-2 mb-2" style={{ maxWidth: 600 }}>
            {editingThumb && (
              <div className="flex flex-col gap-2 mb-3 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
                <Text size="xs" fw={600}>Edit thumbnail URL (admin)</Text>
                <TextInput
                  size="xs"
                  value={thumbDraft}
                  onChange={(e) => setThumbDraft(e.currentTarget.value)}
                  placeholder="https://..."
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button size="xs" loading={savingThumb} onClick={handleSaveThumbnail}>Save</Button>
                  <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingThumb(false)}>Cancel</Button>
                </div>
              </div>
            )}
            {editingImages && (
              <div className="flex flex-col gap-2 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
                <Text size="xs" fw={600}>Edit gallery image URLs — one per line (admin)</Text>
                <Textarea
                  size="xs"
                  autosize
                  minRows={3}
                  maxRows={12}
                  value={imagesDraft}
                  onChange={(e) => setImagesDraft(e.currentTarget.value)}
                  placeholder={'https://...\nhttps://...'}
                />
                <div className="flex gap-2">
                  <Button size="xs" loading={savingImages} onClick={handleSaveImageUrls}>Save</Button>
                  <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingImages(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {isAdmin && !editingImages && (
          <div className="flex justify-center mt-2 mb-2">
            <Button
              size="compact-xs"
              variant="light"
              onClick={() => {
                setImagesDraft(((art.imageUrls) || []).join('\n'));
                setEditingImages(true);
              }}
            >
              Edit gallery images
            </Button>
          </div>
        )}

        {isAdmin && !editingOtherLinks && (
          <>
            <div className="flex justify-center gap-2 mt-2 mb-2">
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  const existing = ((art.otherPlatformsLinks) || []).join('\n');
                  // Seed the textarea with an extra blank line so the
                  // admin can start typing a new URL immediately
                  // without first hitting Enter.
                  setOtherLinksDraft(existing ? existing + '\n' : '');
                  setEditingOtherLinks(true);
                }}
              >
                Specify other platforms URLs
              </Button>
            </div>
            <div className="flex justify-center gap-2 mt-2 mb-2 flex-wrap">
              <Button
                size="compact-xs"
                variant="light"
                color="yellow"
                loading={metacriticSearchLoading}
                onClick={handleMetacriticSearch}
              >
                Search on Metacritic
              </Button>
              <Button
                size="compact-xs"
                variant="light"
                color="orange"
                loading={livelibSearchLoading}
                onClick={handleLivelibSearch}
              >
                Search on Livelib
              </Button>
              <Button
                size="compact-xs"
                variant="light"
                color="lime"
                loading={goodreadsSearchLoading}
                onClick={handleGoodreadsSearch}
              >
                Search on Goodreads
              </Button>
              <Button
                size="compact-xs"
                variant="light"
                color="red"
                loading={kpSearchLoading}
                onClick={handleKinopoiskSearch}
              >
                Search kinopoisk links
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="lime"
                onClick={() => {
                  const q = (art?.name || art?.ruName || '').trim();
                  if (!q) return;
                  const target = `https://www.goodreads.com/search?utf8=%E2%9C%93&query=${encodeURIComponent(q)}`;
                  window.open(target, '_blank', 'noopener,noreferrer');
                }}
              >
                Search on Goodreads manually
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={() => {
                  const q = (art?.name || '').trim();
                  if (!q) return;
                  const target = `https://www.rottentomatoes.com/search?search=${encodeURIComponent(q)}`;
                  window.open(target, '_blank', 'noopener,noreferrer');
                }}
              >
                Search on Rotten Tomatoes
              </Button>
            </div>
          </>
        )}
        {isAdmin && hotPositionLoaded && (
          <div className="mx-auto mt-1 mb-2" style={{ maxWidth: 600 }}>
            <div className="flex items-center gap-2 justify-center flex-wrap">
              <Text size="xs" fw={600}>Hot-arts position (admin)</Text>
              <TextInput
                size="xs"
                w={80}
                value={hotPositionDraft}
                onChange={(e) => setHotPositionDraft(e.currentTarget.value)}
                placeholder="e.g. 1 or -1"
              />
              <Button size="compact-xs" variant="light" loading={hotPositionSaving} onClick={handleSaveHotPosition}>
                Save
              </Button>
              <Text size="xs" c="dimmed">-1 = auto, empty = remove</Text>
            </div>
          </div>
        )}
        {isAdmin && !editingYearGenres && (
          <div className="flex justify-center mt-2 mb-2">
            <Button
              size="compact-xs"
              variant="light"
              onClick={() => {
                setYearDraft(art.year ? String(art.year) : '');
                setGenresDraft((art.genres || []).join('\n'));
                setEditingYearGenres(true);
              }}
            >
              Edit year / genres
            </Button>
          </div>
        )}
        {isAdmin && editingYearGenres && (
          <div className="mx-auto mt-2 mb-2" style={{ maxWidth: 600 }}>
            <div className="flex flex-col gap-2 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
              <Text size="xs" fw={600}>Edit year / genres (admin)</Text>
              <div className="flex items-center gap-2">
                <Text size="xs" w={60}>Year</Text>
                <TextInput
                  size="xs"
                  w={100}
                  value={yearDraft}
                  onChange={(e) => setYearDraft(e.currentTarget.value)}
                  placeholder="e.g. 2024"
                />
              </div>
              <Text size="xs">Genres — one per line</Text>
              <Textarea
                size="xs"
                autosize
                minRows={3}
                maxRows={12}
                value={genresDraft}
                onChange={(e) => setGenresDraft(e.currentTarget.value)}
                placeholder={'Action\nAdventure\nRPG'}
              />
              <div className="flex gap-2">
                <Button size="xs" loading={savingYearGenres} onClick={handleSaveYearGenres}>Save</Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingYearGenres(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}
        {isAdmin && !editingAuthor && (
          <div className="flex justify-center mt-2 mb-2">
            <Button
              size="compact-xs"
              variant="light"
              onClick={() => {
                setAuthorDraft(art.videoGameDeveloper || '');
                setEditingAuthor(true);
              }}
            >
              Edit author / developer
            </Button>
          </div>
        )}
        {isAdmin && editingAuthor && (
          <div className="mx-auto mt-2 mb-2" style={{ maxWidth: 600 }}>
            <div className="flex flex-col gap-2 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
              <Text size="xs" fw={600}>Edit author / developer (admin)</Text>
              <TextInput
                size="xs"
                value={authorDraft}
                onChange={(e) => setAuthorDraft(e.currentTarget.value)}
                placeholder="e.g. FromSoftware"
              />
              <div className="flex gap-2">
                <Button size="xs" loading={savingAuthor} onClick={handleSaveAuthor}>Save</Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingAuthor(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}
        {isAdmin && metacriticSearchResults !== null && (
          <div className="mx-auto mt-1 mb-2" style={{ maxWidth: 600 }}>
            <div className="flex flex-col gap-1 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
              <div className="flex items-center justify-between">
                <Text size="xs" fw={600}>Metacritic matches for "{art?.name}"</Text>
                <Button size="compact-xs" variant="subtle" color="gray" onClick={() => { setMetacriticSearchResults(null); setMetacriticSearchError(null); }}>Close</Button>
              </div>
              {metacriticSearchError && (
                <Text size="xs" c="red">Error: {String(metacriticSearchError).slice(0, 200)}</Text>
              )}
              {metacriticSearchResults.length === 0 && !metacriticSearchError && (
                <Text size="xs" c="dimmed">No results.</Text>
              )}
              {metacriticSearchResults.map((item) => {
                const pathFromApi = item?.criticScoreSummary?.url || '';
                const canonicalPath = pathFromApi.replace(/\/critic-reviews\/?$/, '/');
                const href = canonicalPath ? `https://www.metacritic.com${canonicalPath}` : '';
                const scoreVal = item?.criticScoreSummary?.score;
                const platforms = (item?.platforms || []).map(p => p?.name).filter(Boolean).join(', ');
                const alreadyAdded = !!href && ((art?.otherPlatformsLinks) || []).includes(href);
                return (
                  <div key={item.id} className="flex items-center gap-2 py-0.5">
                    {href && (
                      <Button
                        size="compact-xs"
                        variant="light"
                        color={alreadyAdded ? 'gray' : 'blue'}
                        loading={addingMetacriticUrl === href}
                        disabled={alreadyAdded || (!!addingMetacriticUrl && addingMetacriticUrl !== href)}
                        onClick={() => handleAddMetacriticUrl(item, href)}
                      >
                        {alreadyAdded ? 'Added' : 'Add'}
                      </Button>
                    )}
                    <a href={href} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--mantine-color-blue-text)' }}>
                      {item.title}
                    </a>
                    {item.premiereYear && <Text size="xs" c="dimmed">({item.premiereYear})</Text>}
                    <Text size="xs" c="dimmed">· {item.type}</Text>
                    {typeof scoreVal === 'number' && <Text size="xs" c="dimmed">· Metascore {scoreVal}</Text>}
                    {platforms && <Text size="xs" c="dimmed" className="truncate">· {platforms}</Text>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {isAdmin && livelibSearchResults !== null && (
          <div className="mx-auto mt-1 mb-2" style={{ maxWidth: 600 }}>
            <div className="flex flex-col gap-1 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
              <div className="flex items-center justify-between">
                <Text size="xs" fw={600}>Livelib matches for "{art?.ruName || art?.name}"</Text>
                <Button size="compact-xs" variant="subtle" color="gray" onClick={() => { setLivelibSearchResults(null); setLivelibSearchError(null); }}>Close</Button>
              </div>
              {livelibSearchError && (
                <Text size="xs" c="red">Error: {String(livelibSearchError).slice(0, 200)}</Text>
              )}
              {livelibSearchResults.length === 0 && !livelibSearchError && (
                <Text size="xs" c="dimmed">No results.</Text>
              )}
              {livelibSearchResults.map((item, idx) => {
                const href = item.link || '';
                const alreadyAdded = !!href && ((art?.otherPlatformsLinks) || []).includes(href);
                return (
                  <div key={href || idx} className="flex items-center gap-2 py-0.5">
                    {href && (
                      <Button
                        size="compact-xs"
                        variant="light"
                        color={alreadyAdded ? 'gray' : 'blue'}
                        loading={addingLivelibUrl === href}
                        disabled={alreadyAdded || (!!addingLivelibUrl && addingLivelibUrl !== href)}
                        onClick={() => handleAddLivelibUrl(item)}
                      >
                        {alreadyAdded ? 'Added' : 'Add'}
                      </Button>
                    )}
                    <a href={href} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--mantine-color-blue-text)' }}>
                      {item.title}
                    </a>
                    {item.author && <Text size="xs" c="dimmed">· {item.author}</Text>}
                    {typeof item.rating === 'number' && item.rating > 0 && (
                      <Text size="xs" c="dimmed">· ★ {item.rating.toFixed(1)}</Text>
                    )}
                    {typeof item.ratingsCount === 'number' && item.ratingsCount > 0 && (
                      <Text size="xs" c="dimmed">· {item.ratingsCount.toLocaleString()} reviews</Text>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {isAdmin && goodreadsSearchResults !== null && (
          <div className="mx-auto mt-1 mb-2" style={{ maxWidth: 600 }}>
            <div className="flex flex-col gap-1 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
              <div className="flex items-center justify-between">
                <Text size="xs" fw={600}>Goodreads matches for "{art?.name || art?.ruName}"</Text>
                <Button size="compact-xs" variant="subtle" color="gray" onClick={() => { setGoodreadsSearchResults(null); setGoodreadsSearchError(null); }}>Close</Button>
              </div>
              {goodreadsSearchError && (
                <Text size="xs" c="red">Error: {String(goodreadsSearchError).slice(0, 200)}</Text>
              )}
              {goodreadsSearchResults.length === 0 && !goodreadsSearchError && (
                <Text size="xs" c="dimmed">No results.</Text>
              )}
              {goodreadsSearchResults.map((item, idx) => {
                const href = item.link || '';
                const alreadyAdded = !!href && ((art?.otherPlatformsLinks) || []).includes(href);
                return (
                  <div key={href || idx} className="flex items-center gap-2 py-0.5">
                    {href && (
                      <Button
                        size="compact-xs"
                        variant="light"
                        color={alreadyAdded ? 'gray' : 'blue'}
                        loading={addingGoodreadsUrl === href}
                        disabled={alreadyAdded || (!!addingGoodreadsUrl && addingGoodreadsUrl !== href)}
                        onClick={() => handleAddGoodreadsUrl(item)}
                      >
                        {alreadyAdded ? 'Added' : 'Add'}
                      </Button>
                    )}
                    <a href={href} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--mantine-color-blue-text)' }}>
                      {item.title}
                    </a>
                    {item.author && <Text size="xs" c="dimmed">· {item.author}</Text>}
                    {typeof item.year === 'number' && item.year > 0 && (
                      <Text size="xs" c="dimmed">· {item.year}</Text>
                    )}
                    {typeof item.rating === 'number' && item.rating > 0 && (
                      <Text size="xs" c="dimmed">· ★ {item.rating.toFixed(2)}</Text>
                    )}
                    {typeof item.ratingsCount === 'number' && item.ratingsCount > 0 && (
                      <Text size="xs" c="dimmed">· {item.ratingsCount.toLocaleString()} ratings</Text>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {isAdmin && kpSearchResults !== null && (
          <div className="mx-auto mt-2 mb-2" style={{ maxWidth: 600 }}>
            <div className="flex flex-col gap-1 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
              <div className="flex items-center justify-between">
                <Text size="xs" fw={600}>Kinopoisk matches for "{art?.ruName || art?.name}"</Text>
                <Button size="compact-xs" variant="subtle" color="gray" onClick={() => { setKpSearchResults(null); setKpSearchError(null); }}>Close</Button>
              </div>
              {kpSearchError && (
                <Text size="xs" c="red">Error: {String(kpSearchError).slice(0, 200)}</Text>
              )}
              {kpSearchResults.length === 0 && !kpSearchError && (
                <Text size="xs" c="dimmed">No results.</Text>
              )}
              {kpSearchResults.map((item, idx) => {
                const href = item.url || '';
                const alreadyAdded = !!href && ((art?.otherPlatformsLinks) || []).includes(href);
                return (
                  <div key={href || idx} className="flex items-center gap-2 py-0.5">
                    {href && (
                      <Button
                        size="compact-xs"
                        variant="light"
                        color={alreadyAdded ? 'gray' : 'blue'}
                        loading={addingKpUrl === href}
                        disabled={alreadyAdded || (!!addingKpUrl && addingKpUrl !== href)}
                        onClick={() => handleAddKpUrl(item)}
                      >
                        {alreadyAdded ? 'Added' : 'Add'}
                      </Button>
                    )}
                    <a href={href} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--mantine-color-blue-text)' }}>
                      {item.title || href}
                    </a>
                    {typeof item.year === 'number' && item.year > 0 && (
                      <Text size="xs" c="dimmed">· {item.year}</Text>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {isAdmin && editingOtherLinks && (
          <div className="mx-auto mt-2 mb-2" style={{ maxWidth: 600 }}>
            <div className="flex flex-col gap-2 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
              <Text size="xs" fw={600}>Edit other-platforms URLs — one per line (admin)</Text>
              <Textarea
                size="xs"
                autosize
                minRows={3}
                maxRows={12}
                value={otherLinksDraft}
                onChange={(e) => setOtherLinksDraft(e.currentTarget.value)}
                placeholder={'https://kinopoisk.ru/...\nhttps://imdb.com/...\nhttps://store.steampowered.com/...'}
              />
              <div className="flex gap-2">
                <Button size="xs" loading={savingOtherLinks} onClick={handleSaveOtherPlatformsLinks}>Save</Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingOtherLinks(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mx-auto mt-2 mb-2" style={{ maxWidth: 600 }}>
            {!replaceOpen ? (
              <div className="flex justify-center">
                <Button
                  size="compact-xs"
                  variant="light"
                  color="orange"
                  onClick={() => setReplaceOpen(true)}
                >
                  Replace it in its art events
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
                <Text size="xs" fw={600}>Replace this art's id in all artEvents (admin)</Text>
                <div className="flex gap-2 items-center">
                  <TextInput
                    size="xs"
                    style={{ flex: 1 }}
                    value={replaceInput}
                    onChange={(e) => setReplaceInput(e.currentTarget.value)}
                    placeholder="Other art UUID or /arts/<uuid> URL"
                    autoFocus
                  />
                  {replaceCandidate && (
                    <img
                      src={replaceCandidate.thumbnailUrl}
                      alt={replaceCandidate.name || ''}
                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                    />
                  )}
                </div>
                {replaceCandidate && (
                  <Text size="xs" c="dimmed">{replaceCandidate.name || replaceCandidate.ruName || replaceCandidate.id}</Text>
                )}
                {replaceLookupError && (
                  <Text size="xs" c="red">{replaceLookupError}</Text>
                )}
                <div className="flex gap-2">
                  <Button
                    size="xs"
                    color="orange"
                    loading={replacing}
                    disabled={!replaceCandidate}
                    onClick={handleReplaceInEvents}
                  >
                    Replace in art events
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={() => {
                      setReplaceOpen(false);
                      setReplaceInput('');
                      setReplaceCandidate(null);
                      setReplaceLookupError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {isViewerOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closeImageViewer}
          >
            <Button
              variant="subtle"
              size="sm"
              className="absolute top-4 right-4 z-10"
              style={{ color: 'white', fontSize: '1.5rem' }}
              onClick={(e) => {
                e.stopPropagation();
                closeImageViewer();
              }}
            >
              &times;
            </Button>
            {/* Image area — fits inside the viewport with room reserved
                for the counter below and the close/paging controls.
                Previous version used md:max-h-[80%] (desktop-only!) so
                a tall portrait image overflowed the viewport on mobile
                and had to be scrolled. Pin to viewport units instead
                so the constraint applies everywhere. */}
            <div
              className="relative flex items-center justify-center"
              style={{
                maxWidth: '100vw',
                maxHeight: 'calc(100vh - 4rem)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
                src={images[currentImageIndex]}
                alt={`Art Image ${currentImageIndex + 1}`}
              />
              <div
                className="absolute inset-y-0 left-0 w-1/2 cursor-pointer"
                onClick={showPreviousImage}
              />
              <div
                className="absolute inset-y-0 right-0 w-1/2 cursor-pointer"
                onClick={showNextImage}
              />
            </div>
            {images.length > 1 && (
              <div
                className="absolute left-0 right-0 text-white text-lg text-center pointer-events-none"
                style={{ bottom: '1rem' }}
              >
                {`${currentImageIndex + 1} / ${images.length}`}
              </div>
            )}
          </div>
        )}
        {isVideoViewerOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closeVideoViewer}
          >
            <Button
              variant="subtle"
              size="sm"
              className="absolute top-4 right-4"
              style={{ color: 'white', fontSize: '1.5rem' }}
              onClick={(e) => {
                e.stopPropagation();
                closeVideoViewer();
              }}
            >
              &times;
            </Button>

            <div
              className="relative md:max-h-[80%] md:max-w-[80%] flex flex-col items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* <div
                className="absolute inset-y-0 left-0 w-1/2 cursor-pointer"
                onClick={showPreviousVideo}
              /> */}
              <div className="max-h-full max-w-full w-full md:w-auto flex items-center justify-center">
                {(() => {
                  const current = videos[currentVideoIndex];
                  const ytEmbed = getYouTubeEmbedUrl(current);
                  if (ytEmbed) {
                    const watchUrl = ytEmbed.replace("/embed/", "/watch?v=");
                    return (
                      <Text
  component="a"
  href={watchUrl}
  target="_blank"
  rel="noopener noreferrer"
  c="blue"
  fw={600}
  size="xl"
  className="hover:shadow-md transition-all duration-200"
>
  {language === "ru" ? "Открыть в YouTube" : "Open in YouTube"}
</Text>
                    )

                    // YouTube embed
                    return (
                      <iframe
                        title={`video-${currentVideoIndex}`}
                        src={ytEmbed}
                        className="w-full md:w-auto max-h-full max-w-full"
                        style={{ minHeight: '200px' }}
                        frameBorder="0"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  } else if (isDirectVideoFile(current)) {
                    // direct video file
                    return (
                      <video
                        className="max-h-full max-w-full w-full md:w-auto"
                        src={current}
                        controls
                        autoPlay
                      />
                    );
                  } else {
                    // fallback: try to embed URL in an iframe (some sites allow embedding)
                    return (
                      <iframe
                        title={`video-fallback-${currentVideoIndex}`}
                        src={current}
                        className="w-full md:w-auto max-h-full max-w-full"
                        style={{ minHeight: '200px' }}
                        frameBorder="0"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  }
                })()}
              </div>

              {/* <div
                className="absolute inset-y-0 right-0 w-1/2 cursor-pointer"
                onClick={showNextVideo}
              /> */}
              {/* <div className="mt-2 text-white text-lg text-center w-full">
                {videos && videos.length > 0 ? `${currentVideoIndex + 1} / ${videos.length}` : ""}
              </div> */}
            </div>
          </div>
        )}

        <div className="text-3xl flex flex-wrap items-center justify-center text-center gap-2">
          <b>{localizeArt(art, language).name + formatYear(art.year, language)}</b>
        </div>
        {art.artTags && <ArtTags tags={art.artTags} language={language} />}
        {isAdmin && !editingName && (
          <div className="flex justify-center mt-2">
            <Button
              size="compact-xs"
              variant="light"
              onClick={() => {
                setNameDraftEn(art.name || '');
                setNameDraftRu(art.ruName || '');
                setEditingName(true);
              }}
            >
              Specify titles
            </Button>
          </div>
        )}
        {isAdmin && editingName && (
          <div className="flex flex-col gap-2 mt-2 p-3 rounded mx-auto w-full max-w-2xl" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
            <Text size="xs" fw={600}>Edit titles (admin)</Text>
            <Text size="xs" c="dimmed">English (name)</Text>
            <TextInput
              size="xs"
              value={nameDraftEn}
              onChange={(e) => setNameDraftEn(e.currentTarget.value)}
              placeholder="English title..."
            />
            <Text size="xs" c="dimmed">Русский (ruName)</Text>
            <TextInput
              size="xs"
              value={nameDraftRu}
              onChange={(e) => setNameDraftRu(e.currentTarget.value)}
              placeholder="Название на русском..."
            />
            <div className="flex gap-2">
              <Button size="xs" loading={savingName} onClick={handleSaveName}>Save</Button>
              <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingName(false)}>Cancel</Button>
            </div>
          </div>
        )}
        <div className="my-1 flex justify-center text-center space-x-2 md:space-x-3">
          <PlatformRatings art={art} fullMode={true} />
        </div>

        {isAdmin && (
          <AdminRatingsEditor art={art} onArtUpdated={fetchArt} />
        )}

        {
        art.watchLink &&
        <div className="flex justify-center text-center space-x-2 md:space-x-3 mt-2">
          <Text
            component="a"
            href={art.watchLink}
            target="_blank"
            rel="noopener noreferrer"
            c="blue"
            className="hover:underline"
          >
            {language === "ru" ? "Смотреть" : "Watch"}
          </Text>
        </div>
        }


        
        <div className="mx-auto w-full max-w-2xl px-4 md:px-6">
          <div className="my-2 flex flex-col items-center text-center space-y-1">
            {
            art.subCategory === "series" ? (
              (() => {
                const hasLast = art.lastEpisodeTime && new Date(art.lastEpisodeTime).getTime() !== 0;
                const hasNext = art.nextEpisodeTime && new Date(art.nextEpisodeTime).getTime() !== 0;
                return (
                  <>
                    <Text c="dimmed">
                      {language === "ru" ? "Сериал" : "TV series"}
                      {art.year && (
                        language === "ru" ? ", вышел в " + art.year : ", released in " + art.year
                      )}
                    </Text>
                    {(hasLast || hasNext) && (
                      <Text c="dimmed">
                        {hasLast && (
                          language === "ru" ? "Последняя серия: " + formatDate(art.lastEpisodeTime, language) : "Last episode: " + formatDate(art.lastEpisodeTime, language)
                        )}
                        {hasLast && hasNext && <br/>}
                        {hasNext && (
                          language === "ru" ? "Следующая серия: " + formatDate(art.nextEpisodeTime, language) : "Next episode: " + formatDate(art.nextEpisodeTime, language)
                        )}
                      </Text>
                    )}
                  </>
                );
              })()
            ) : (
              art.category === "3" && (
                <Text c="dimmed">
                  {art.subCategory}
                </Text>
              )
            )
            }
            {localizeArt(art, language).genres && (
              <Text c="dimmed">
                <b>{language === "ru" ? "Жанр:" : "Genre:"}</b> {localizeArt(art, language).genres.join(", ")}
              </Text>
            )}
            {art.duration !== 0 && (
              <Text c="dimmed">
                <b>{language === "ru" ? "Длительность:" : "Duration:"}</b> {formatDuration(art.duration, language)}
              </Text>
            )}
            {art.videoGameDeveloper && (
                <Text c="dimmed">
                  <b>
                    {art.category === 1
                      ? language === "ru" ? "Режиссер:" : "Director:"
                      : art.category === 2
                      ? language === "ru" ? "Разработчик:" : "Developer:"
                      : language === "ru" ? "Автор:" : "Author:"}
                  </b>
                  <Text component={Link} c="blue" to={'/addEvent?limit=200&term=author:'+encodeURIComponent(localizeArt(art, language).videoGameDeveloper)}>
                    &nbsp;{localizeArt(art, language).videoGameDeveloper}
                  </Text>
                </Text>
            )}
            {localizeArt(art, language).actors && (
              <Text c="dimmed">
                <b>{language === "ru" ? "Актёры:" : "Actors:"}</b>{" "}
                {localizeArt(art, language).actors.map((actor, index) => (
                  <span key={actor}>
                    <Text component={Link} c="blue" to={'/addEvent?limit=200&term=actor:'+encodeURIComponent(actor)}>
                      {actor}
                    </Text>
                    {index < localizeArt(art, language).actors.length - 1 && ", "}
                  </span>
                ))}
              </Text>
            )}
            {localizeArt(art, language).country && (
              <Text c="dimmed">
                <b>{language === "ru" ? "Страна:" : "Country:"}</b> {countries.getName(localizeArt(art, language).country, language) || localizeArt(art, language).country}
              </Text>
            )}
          </div>
        </div>



        <div className="mx-auto w-full max-w-2xl px-4 md:px-6">
          <div className="flex justify-center whitespace-pre-wrap">
            <div className="text-left leading-relaxed" style={{ maxWidth: '65ch' }}>
            {
            language === "ru"
              ? (art.ruDescription !== '' ? renderDescription(art.ruDescription) : (
                  loadingDescription
                    ? <div className="flex flex-col items-center gap-2"><Loader size="sm" /><Text c="dimmed" size="sm">Описание готовится...</Text></div>
                    : "Описание готовится..."
                ))
              : (art.description !== '' ? renderDescription(art.description) : (
                  loadingDescription
                    ? <div className="flex flex-col items-center gap-2"><Loader size="sm" /><Text c="dimmed" size="sm">Description is being prepared...</Text></div>
                    : "Description is being prepared..."
                ))
            }
            </div>
          </div>
          {isAdmin && !editingDesc && (
            <div className="flex justify-center mt-2">
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  setDescDraftEn(art.description || '');
                  setDescDraftRu(art.ruDescription || '');
                  setEditingDesc(true);
                }}
              >
                Specify description
              </Button>
            </div>
          )}
          {isAdmin && editingDesc && (
            <div className="flex flex-col gap-2 mt-2 p-3 rounded" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
              <Text size="xs" fw={600}>Edit description (admin)</Text>
              <Text size="xs" c="dimmed">English</Text>
              <Textarea
                size="xs"
                autosize
                minRows={3}
                maxRows={20}
                value={descDraftEn}
                onChange={(e) => setDescDraftEn(e.currentTarget.value)}
                placeholder="English description..."
              />
              <Text size="xs" c="dimmed">Русский</Text>
              <Textarea
                size="xs"
                autosize
                minRows={3}
                maxRows={20}
                value={descDraftRu}
                onChange={(e) => setDescDraftRu(e.currentTarget.value)}
                placeholder="Описание на русском..."
              />
              <div className="flex gap-2">
                <Button size="xs" loading={savingDesc} onClick={handleSaveDescription}>Save</Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingDesc(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {myUser && (
        <div className="mt-4 mb-4" style={{ borderTop: '1px solid var(--mantine-color-default-border)', borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <div className="mt-4 mb-4 text-xl">
            <div>
              <CardEventArtChatMini art={art} />

              {/* Paper-plane + engaged-friends strip. */}
              <div ref={paperplaneSectionRef} className="flex flex-col items-center mt-7 gap-2">
                {(() => {
                  const MAX_VISIBLE = 12;
                  const events = otherPeopleEvents || [];
                  const visible = events.slice(0, MAX_VISIBLE);
                  const overflow = events.length - visible.length;
                  const hasAny = events.length > 0;
                  return (
                    <div
                      className={`flex flex-wrap justify-center items-center gap-2 px-3 py-1.5 rounded-2xl max-w-full transition-colors${hasAny ? '' : ' cursor-pointer'}`}
                      role={hasAny ? undefined : 'button'}
                      tabIndex={hasAny ? undefined : 0}
                      style={{ background: pillBg(colorScheme), minHeight: '48px' }}
                      onClick={hasAny ? undefined : () => setIsDropdownVisible((prev) => !prev)}
                      onKeyDown={hasAny ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsDropdownVisible((prev) => !prev); } }}
                      // When populated, individual avatars are the click
                      // targets; only the paperplane icon at the end drives
                      // the pill hover (see below). When empty, the whole
                      // pill is one big button so we also tint on pill
                      // enter/leave.
                      onMouseEnter={hasAny ? undefined : (e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
                      onMouseLeave={hasAny ? undefined : (e) => { e.currentTarget.style.background = pillBg(colorScheme); }}
                    >
                      {hasAny ? (
                        <>
                          {/* Label the row — avatars + a paperplane
                              icon on their own gave no cue what the row
                              IS. "In friends' libraries:" is the one
                              short phrase that makes the whole strip
                              self-explanatory. */}
                          <Text size="xs" c="dimmed" mr={2}>
                            {language === "ru" ? "В библиотеке:" : "In libraries:"}
                          </Text>
                          {visible.map((ev) => {
                            const isSelected = selectedInlineUserId === ev.userId;
                            const tagIconName = (ev.eventTagNames || []).find(isGlobalManualTag);
                            const tagIcon = tagIconName ? tagName(tagIconName, language, undefined, true) : null;
                            return (
                              <div
                                key={ev.artEventId}
                                className="relative"
                                title={ev.userName}
                              >
                                <img
                                  src={ev.userAvatarUrl}
                                  alt={ev.userName}
                                  className="w-[41.21px] h-[41.21px] rounded-full cursor-pointer transition-transform hover:scale-105 hover:brightness-110 block"
                                  style={{
                                    border: isSelected
                                      ? '2px solid var(--mantine-primary-color-filled)'
                                      : '2px solid var(--mantine-color-default-border)',
                                    transform: isSelected ? 'scale(1.05)' : undefined,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedInlineUserId((prev) => (prev === ev.userId ? null : ev.userId));
                                  }}
                                />
                                {tagIcon && (
                                  <span
                                    className="absolute pointer-events-none flex items-center justify-center"
                                    style={{
                                      right: -4,
                                      bottom: -4,
                                      lineHeight: 1,
                                      fontSize: 14.7,
                                      transform: 'scale(1.05)',
                                      transformOrigin: 'bottom right',
                                      textShadow: '0 0 3px var(--mantine-color-body), 0 0 3px var(--mantine-color-body)',
                                    }}
                                  >
                                    {tagIcon}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {overflow > 0 && (
                            <span
                              className="text-xs font-semibold flex items-center justify-center rounded-full"
                              style={{ minWidth: 41.21, height: 41.21, background: 'var(--mantine-color-default)', color: 'var(--mantine-color-dimmed)' }}
                            >
                              +{overflow}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-sm italic" style={{ color: 'var(--mantine-color-dimmed)' }}>
                          {language === "ru" ? "Никто не добавлял в библиотеку" : "No people added to their library"}
                        </span>
                      )}
                      <div
                        role="button"
                        tabIndex={0}
                        // stopPropagation: when the row is empty the pill
                        // itself has an onClick that toggles the overlay.
                        // Without stopping, the paperplane click would
                        // bubble up and toggle twice, netting no-op.
                        onClick={(e) => { e.stopPropagation(); setIsDropdownVisible((prev) => !prev); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setIsDropdownVisible((prev) => !prev); } }}
                        className="relative flex items-center ml-1 cursor-pointer rounded-full p-1"
                        title={language === "ru" ? "Рекомендовать друзьям" : "Recommend to friends"}
                        // Hover tints the whole pill (parentElement), not
                        // just the circular area around the icon.
                        onMouseEnter={(e) => {
                          const pill = e.currentTarget.parentElement;
                          if (pill) pill.style.background = 'var(--mantine-color-default-hover)';
                        }}
                        onMouseLeave={(e) => {
                          const pill = e.currentTarget.parentElement;
                          if (pill) pill.style.background = pillBg(colorScheme);
                        }}
                      >
                        <img
                          src="/send_plane_icon.png"
                          alt={language === "ru" ? "Поиск людей" : "Person search"}
                          className="w-[31.90px] h-[31.90px]"
                          style={{ filter: 'var(--icon-filter)' }}
                        />
                        {otherPeopleEventsLoading && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--mantine-color-dimmed)', borderTopColor: 'transparent' }}></span>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {selectedInlineUserId && (
                  <div className="inline-flex flex-col items-stretch gap-1.5 max-w-full">
                    {renderArtUserActionButtons({
                      selectedUsers: [selectedInlineUserId],
                      language,
                      hasEvent,
                      hasRecommendationFromMe,
                      hasRecommendationToMe,
                      recommendationsFromMeMap,
                      recommendationsToMeMap,
                      messagesSentToUser,
                      sendingToUser,
                      navigate,
                      handleRecommend,
                      navigateToDiscussion,
                      sendArtLinkToUser,
                      goToDirectChat,
                      getUserName: (uid) => (otherPeopleEvents || []).find((e) => e.userId === uid)?.userName,
                    })}
                  </div>
                )}
                {isDropdownVisible && (
                  <FollowingDropdown
                    isOpen={isDropdownVisible}
                    onClose={() => setIsDropdownVisible(false)}
                    language={language}
                    art={art}
                    otherPeopleEvents={otherPeopleEvents || []}
                    recommendationsFromMeMap={recommendationsFromMeMap}
                    recommendationsToMeMap={recommendationsToMeMap}
                    messagesSentToUser={messagesSentToUser}
                    sendingToUser={sendingToUser}
                    handleRecommend={handleRecommend}
                    navigateToDiscussion={navigateToDiscussion}
                    sendArtLinkToUser={sendArtLinkToUser}
                    goToDirectChat={goToDirectChat}
                    hasEvent={hasEvent}
                    hasRecommendationFromMe={hasRecommendationFromMe}
                    hasRecommendationToMe={hasRecommendationToMe}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* "Похожие" / "Similar" anchor: dimmed divider on either side so
            the title reads as a section header, not a floating word. */}
        <div className="mt-8 flex items-center gap-3 px-2" style={{ color: 'var(--mantine-color-dimmed)' }}>
          <div className="flex-1 h-px" style={{ background: 'var(--mantine-color-default-border)' }} />
          <span className="font-semibold text-base uppercase tracking-wider" style={{ color: 'var(--mantine-color-text)' }}>
            {language === "ru" ? "Похожие" : "Similar"}
          </span>
          <div className="flex-1 h-px" style={{ background: 'var(--mantine-color-default-border)' }} />
        </div>
        {myUser && myUser.myUserInfo.myUserId === "4312f009-7502-43a6-9483-2824508fe96f" && (
          <div className="flex flex-col items-center mb-2">
            <Button
              variant="light"
              size="xs"
              loading={recalcLoading}
              onClick={() => handleRecalcSimilars(art.id)}
            >
              Recalc similars
            </Button>
          </div>
        )}
        {
        similars !== null && similars.length > 0
        ?
          <div className="w-full">
            <ImageScroller key={art.id} arts={similars} scrollKey={art.id} />
          </div>
        :
          similars !== null
          ?
            null
          :
            <div className="flex justify-center text-center">
              <div className="w-full max-w-md">
                <div className="h-2 w-full overflow-hidden rounded-full" style={{ background: 'var(--mantine-color-default)' }}>
                  <div className="similars-progress-bar h-full w-1/3 rounded-full" style={{ background: 'var(--mantine-color-default-border)' }}></div>
                </div>
              </div>
            </div>
        }
    </div>
    </>
  );
}

export default ArtPage;
